import { onMounted, onUnmounted, ref, computed, watch } from "vue";
import { useTransport } from "./useTransporter";
import { usePage } from "@inertiajs/vue3";

export interface RenderBroadcast<T> {
    fromClient: string;
    data: T;
    timestamp: string
}

export function useLiveRender<T>(onUpdate: (data: RenderBroadcast<T>) => void, channel?: string) {
    const loading = ref(true);
    const connectionError = ref<string | null>(null);
    const reconnectAttempts = ref(0);
    const maxReconnectAttempts = 5;
    const reconnectDelay = ref(1000);
    const isJoined = ref(false);




    const { connect, connected, connecting, lastError, disconnect, ...transporter } = useTransport({
        url: usePage().props.__node ?? 'http://localhost:3001',
        autoConnect: true,
        transports: ['websocket'],
        enableOnMounted: false,
        reconnectionAttempts: maxReconnectAttempts,
        reconnectionDelayMax: 10000
    });

    const connectionStatus = computed(() => {
        if (connecting.value) return 'connecting';
        if (connected.value && isJoined.value) return 'connected';
        if (connectionError.value) return 'error';
        return 'disconnected';
    });

    const shouldShowLoading = computed(() => {
        return loading.value || connecting.value;
    });

    const reconnectWithBackoff = () => {
        if (reconnectAttempts.value >= maxReconnectAttempts) {
            connectionError.value = `Failed to connect after ${maxReconnectAttempts} attempts`;
            loading.value = false;
            return;
        }

        reconnectAttempts.value++;
        const delay = Math.min(reconnectDelay.value * Math.pow(2, reconnectAttempts.value - 1), 10000);

        setTimeout(() => {
            if (!connected.value && !connecting.value) {
                connect();
            }
        }, delay);
    };

    const setupRenderBroadcastListener = function () {
        transporter.on('connect', function () {
            connectionError.value = null;
            reconnectAttempts.value = 0;
            isJoined.value = false;



            if (channel) {
                transporter.join(channel, (ack: any) => {
                    if (ack) {
                        isJoined.value = true;
                        loading.value = false;
                        console.debug('Successfully joined channel:', channel);
                    }
                });
            } else
                loading.value = false
        })



        transporter.on('disconnect', function (reason?: string) {
            console.debug('Disconnected:', reason);
            isJoined.value = false;

            if (reason === 'io server disconnect') {
                connectionError.value = 'Server disconnected';
            }
        })


        transporter.on('reconnect_attempt', function (attempt: any) {
            console.debug('Reconnection attempt:', attempt);
            connectionError.value = null;
        })



        transporter.on('connect_error', function (err: any) {
            console.debug('Connection Error:', err);
            connectionError.value = err.message || 'Connection failed';
            loading.value = false;
            reconnectWithBackoff();
        })



        // Only set up the render:broadcast listener since connection events are handled by useTransport
        transporter.on('render:broadcast', (data: RenderBroadcast<T>) => {
            onUpdate(data);
        });

        transporter.on('reconnect', (attempt: any) => {
            console.debug('Reconnected after', attempt, 'attempts');
            reconnectAttempts.value = 0;
            connectionError.value = null;
        });

        transporter.on('reconnect_failed', () => {
            connectionError.value = 'Reconnection failed';
            loading.value = false;
        });

        connect();
    };

    watch(lastError, (error) => {
        if (error) {
            connectionError.value = error.message || 'Unknown connection error';
            loading.value = false;
        }
    });

    const retry = () => {
        reconnectAttempts.value = 0;
        connectionError.value = null;
        loading.value = true;
        connect();
    };

    onMounted(function () {
        // Live render is only relevant for CMS editor preview sessions, which
        // pass a `channel`. Without one there is nothing to subscribe to, so
        // don't open a socket at all — otherwise every normal page load tries
        // (and fails) to connect, e.g. ERR_CONNECTION_REFUSED to the node server.
        if (!channel) {
            loading.value = false;
            return;
        }

        setupRenderBroadcastListener();
    });

    onUnmounted(function () {
        if (channel && isJoined.value) {
            transporter.leave(channel);
        }
        disconnect();

        // Clear any pending reconnection timeouts
        if (reconnectAttempts.value > 0) {
            reconnectAttempts.value = 0;
        }

        // Reset state
        isJoined.value = false;
        connectionError.value = null;
        loading.value = true;
    });

    return {
        loading: shouldShowLoading,
        connected,
        connecting,
        connect,
        disconnect,
        connectionError,
        connectionStatus,
        reconnectAttempts,
        maxReconnectAttempts,
        retry
    };
}